import React from 'react';
import { Box, Container, Typography, LinearProgress } from '@mui/material';

const BAR_HEIGHT = 26;

// ─── Estilos propios del componente (sx de MUI) ──────────────────────────────
const styles = {
  stickyBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1201, // por encima de AppBars/Drawers típicos de MUI
    background: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.10)',
  },
  stickyInner: {
    padding: 'clamp(0.5rem, 1vw, 1rem) clamp(0.75rem, 3vw, 1.5rem)',
  },
  progressWrapper: {
    position: 'relative',
    height: BAR_HEIGHT,
  },
  bar: {
    height: BAR_HEIGHT,
    borderRadius: BAR_HEIGHT / 2,
    backgroundColor: 'grey.300',
    '& .MuiLinearProgress-bar': {
      borderRadius: BAR_HEIGHT / 2,
      background: 'linear-gradient(90deg, #42a5f5 0%, #1976d2 100%)',
    },
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '4px',
    paddingInline: '6px',
    pointerEvents: 'none', // no interfiere con clicks/scroll debajo
  },
  percentChip: {
    flexShrink: 0,
    lineHeight: 1,
    fontWeight: 700,
    fontSize: '0.7rem',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.45)', // contraste garantizado sobre track o relleno
    borderRadius: '999px',
    padding: '3px 8px',
  },
};

// Estilo del label, dependiente de si hay avance o no.
// Sin avance: texto oscuro y sin sombra, para leerse sobre el track gris claro.
// Con avance: texto blanco con sombra, para leerse sobre el relleno azul.
const getLabelSx = (isEmpty) => ({
  lineHeight: 1,
  fontWeight: 600,
  fontSize: '0.72rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  color: isEmpty ? 'grey.600' : '#fff',
  textShadow: isEmpty ? 'none' : '0 1px 2px rgba(0,0,0,0.45)',
  transition: 'color 0.2s ease',
});

/**
 * Barra de progreso fija (sticky) que muestra el porcentaje de avance
 * de la encuesta. El label y el % van superpuestos DENTRO de la barra
 * para reducir el alto total del componente.
 * - El label cambia de color oscuro→blanco según si hay avance, porque
 *   queda cerca del inicio y rápidamente pasa a estar sobre el relleno.
 * - El % va en un chip con fondo oscuro semitransparente fijo, porque
 *   queda cerca del final y se mantiene sobre el track vacío hasta
 *   que el progreso está casi completo; así siempre es legible.
 */
const ProgressBarUser = React.memo(({ progress, label }) => {
  const isEmpty = !progress || progress <= 0;

  return (
    <Box sx={styles.stickyBar}>
      <Container maxWidth="md" disableGutters sx={styles.stickyInner}>
        <Box sx={styles.progressWrapper}>
          <LinearProgress variant="determinate" value={progress} sx={styles.bar} />
          <Box sx={styles.overlay}>
            <Typography component="span" sx={getLabelSx(isEmpty)}>
              {label}
            </Typography>
            <Typography component="span" sx={styles.percentChip}>
              {progress}%
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

ProgressBarUser.displayName = 'ProgressBarUser';

export default ProgressBarUser;